import React, {useEffect, useRef, useState} from 'react';
import styles from '../AppointmentManagement/AppointmentManagement.module.css';
import {UploadIcon} from '../icons';

interface InsuranceCardUploadProps {
  onUpload: (frontFile?: File, backFile?: File) => Promise<void>;
  frontFile?: File | null;
  backFile?: File | null;
  uploadStatus?: string;
}

export const InsuranceCardUpload: React.FC<InsuranceCardUploadProps> = ({
  onUpload,
  frontFile,
  backFile,
  uploadStatus,
}) => {
  const frontInputRef = useRef<HTMLInputElement>(null);
  const backInputRef = useRef<HTMLInputElement>(null);
  const [isUploadingFront, setIsUploadingFront] = useState(false);
  const [isUploadingBack, setIsUploadingBack] = useState(false);
  const [localFrontFile, setLocalFrontFile] = useState<File | null>(frontFile ?? null);
  const [localBackFile, setLocalBackFile] = useState<File | null>(backFile ?? null);
  const [frontError, setFrontError] = useState<string | null>(null);
  const [backError, setBackError] = useState<string | null>(null);
  const [frontUploadedToServer, setFrontUploadedToServer] = useState(false);
  const [backUploadedToServer, setBackUploadedToServer] = useState(false);
  const [frontPreviewUrl, setFrontPreviewUrl] = useState<string | null>(null);
  const [backPreviewUrl, setBackPreviewUrl] = useState<string | null>(null);
  const frontPreviewUrlRef = useRef<string | null>(null);
  const backPreviewUrlRef = useRef<string | null>(null);

  // Server status only indicates if BOTH cards are uploaded and verified
  // We rely on local file state to show individual card upload status
  const bothUploadedOnServer = uploadStatus === 'uploaded' || uploadStatus === 'verified';

  // Sync with props when they change
  useEffect(() => {
    if (frontFile !== undefined) {
      setLocalFrontFile(frontFile);
      // If frontFile is null (cleared), reset the upload flag and preview
      if (frontFile === null) {
        setFrontUploadedToServer(false);
        if (frontPreviewUrlRef.current) {
          URL.revokeObjectURL(frontPreviewUrlRef.current);
          frontPreviewUrlRef.current = null;
          setFrontPreviewUrl(null);
        }
      } else if (frontFile instanceof File && frontFile.size > 0) {
        // Only create preview URL if file has actual content
        // Clean up previous preview URL
        if (frontPreviewUrlRef.current) {
          URL.revokeObjectURL(frontPreviewUrlRef.current);
        }
        // Create preview URL for new file
        const url = URL.createObjectURL(frontFile);
        frontPreviewUrlRef.current = url;
        setFrontPreviewUrl(url);
      } else if (frontFile instanceof File && frontFile.size === 0) {
        // Empty file (e.g., from localStorage after refresh) - clear preview
        if (frontPreviewUrlRef.current) {
          URL.revokeObjectURL(frontPreviewUrlRef.current);
          frontPreviewUrlRef.current = null;
          setFrontPreviewUrl(null);
        }
      }
    }
  }, [frontFile]);

  useEffect(() => {
    if (backFile !== undefined) {
      setLocalBackFile(backFile);
      // If backFile is null (cleared), reset the upload flag and preview
      if (backFile === null) {
        setBackUploadedToServer(false);
        if (backPreviewUrlRef.current) {
          URL.revokeObjectURL(backPreviewUrlRef.current);
          backPreviewUrlRef.current = null;
          setBackPreviewUrl(null);
        }
      } else if (backFile instanceof File && backFile.size > 0) {
        // Only create preview URL if file has actual content
        // Clean up previous preview URL
        if (backPreviewUrlRef.current) {
          URL.revokeObjectURL(backPreviewUrlRef.current);
        }
        // Create preview URL for new file
        const url = URL.createObjectURL(backFile);
        backPreviewUrlRef.current = url;
        setBackPreviewUrl(url);
      } else if (backFile instanceof File && backFile.size === 0) {
        // Empty file (e.g., from localStorage after refresh) - clear preview
        if (backPreviewUrlRef.current) {
          URL.revokeObjectURL(backPreviewUrlRef.current);
          backPreviewUrlRef.current = null;
          setBackPreviewUrl(null);
        }
      }
    }
  }, [backFile]);

  // Clean up preview URLs on unmount
  useEffect(() => {
    return () => {
      if (frontPreviewUrlRef.current) {
        URL.revokeObjectURL(frontPreviewUrlRef.current);
      }
      if (backPreviewUrlRef.current) {
        URL.revokeObjectURL(backPreviewUrlRef.current);
      }
    };
  }, []);

  const handleFrontClick = () => {
    frontInputRef.current?.click();
  };

  const handleBackClick = () => {
    backInputRef.current?.click();
  };

  const handleFrontFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setIsUploadingFront(true);
      setFrontError(null);
      try {
        // Only send the back file if it hasn't been uploaded yet and has actual content
        const backFileToSend =
          !backUploadedToServer && localBackFile && localBackFile.size > 0 ? localBackFile : undefined;
        await onUpload(file, backFileToSend);
        setLocalFrontFile(file);
        // Create preview URL for the uploaded file
        if (frontPreviewUrlRef.current) {
          URL.revokeObjectURL(frontPreviewUrlRef.current);
        }
        const previewUrl = URL.createObjectURL(file);
        frontPreviewUrlRef.current = previewUrl;
        setFrontPreviewUrl(previewUrl);
        setFrontUploadedToServer(true);
        // Reset input after successful upload to allow re-uploading if needed
        if (frontInputRef.current) {
          frontInputRef.current.value = '';
        }
      } catch (error) {
        console.error('Error uploading front card:', error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to upload front card';
        setFrontError(errorMessage);
        setLocalFrontFile(null);
        // Clean up preview URL on error
        if (frontPreviewUrlRef.current) {
          URL.revokeObjectURL(frontPreviewUrlRef.current);
          frontPreviewUrlRef.current = null;
          setFrontPreviewUrl(null);
        }
        // Reset input on error
        if (frontInputRef.current) {
          frontInputRef.current.value = '';
        }
      } finally {
        setIsUploadingFront(false);
      }
    }
  };

  const handleBackFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setIsUploadingBack(true);
      setBackError(null);
      try {
        // Only send the front file if it hasn't been uploaded yet and has actual content
        const frontFileToSend =
          !frontUploadedToServer && localFrontFile && localFrontFile.size > 0 ? localFrontFile : undefined;
        await onUpload(frontFileToSend, file);
        setLocalBackFile(file);
        // Create preview URL for the uploaded file
        if (backPreviewUrlRef.current) {
          URL.revokeObjectURL(backPreviewUrlRef.current);
        }
        const previewUrl = URL.createObjectURL(file);
        backPreviewUrlRef.current = previewUrl;
        setBackPreviewUrl(previewUrl);
        setBackUploadedToServer(true);
        // Reset input after successful upload to allow re-uploading if needed
        if (backInputRef.current) {
          backInputRef.current.value = '';
        }
      } catch (error) {
        console.error('Error uploading back card:', error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to upload back card';
        setBackError(errorMessage);
        setLocalBackFile(null);
        // Clean up preview URL on error
        if (backPreviewUrlRef.current) {
          URL.revokeObjectURL(backPreviewUrlRef.current);
          backPreviewUrlRef.current = null;
          setBackPreviewUrl(null);
        }
        // Reset input on error
        if (backInputRef.current) {
          backInputRef.current.value = '';
        }
      } finally {
        setIsUploadingBack(false);
      }
    }
  };

  const isFrontUploaded = localFrontFile !== null || bothUploadedOnServer;
  const isBackUploaded = localBackFile !== null || bothUploadedOnServer;
  const isBackDisabled = !isFrontUploaded || isUploadingFront;

  return (
    <div className={styles.cardUploadContainer}>
      <input
        ref={frontInputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg"
        onChange={handleFrontFileChange}
        className={styles.hiddenInput}
        disabled={isUploadingFront || isUploadingBack}
      />
      <input
        ref={backInputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg"
        onChange={handleBackFileChange}
        className={styles.hiddenInput}
        disabled={isBackDisabled || isUploadingBack}
      />

      <div className={styles.cardUploadWrapper}>
        <button
          type="button"
          onClick={handleFrontClick}
          className={`${styles.cardUploadButton} ${isFrontUploaded ? styles.cardUploadButtonUploaded : ''} ${isUploadingFront ? styles.cardUploadButtonUploading : ''} ${frontError ? styles.cardUploadButtonError : ''}`}
          disabled={isUploadingFront || isUploadingBack || bothUploadedOnServer}
        >
          {isUploadingFront ? (
            <>
              <div className={styles.spinner} />
              <span>Uploading...</span>
            </>
          ) : frontPreviewUrl ? (
            <img src={frontPreviewUrl} alt="Front of insurance card" className={styles.cardThumbnail} />
          ) : (
            <>
              <UploadIcon />
              <span>{isFrontUploaded ? 'Front uploaded' : 'Front of card'}</span>
            </>
          )}
        </button>
        {frontError ? (
          <div className={styles.errorMessage}>
            <span>{frontError}</span>
          </div>
        ) : null}
      </div>

      <div className={styles.cardUploadWrapper}>
        <button
          type="button"
          onClick={handleBackClick}
          className={`${styles.cardUploadButton} ${isBackUploaded ? styles.cardUploadButtonUploaded : ''} ${isUploadingBack ? styles.cardUploadButtonUploading : ''} ${isBackDisabled ? styles.cardUploadButtonDisabled : ''} ${backError ? styles.cardUploadButtonError : ''}`}
          disabled={isBackDisabled || isUploadingBack || bothUploadedOnServer}
        >
          {isUploadingBack ? (
            <>
              <div className={styles.spinner} />
              <span>Uploading...</span>
            </>
          ) : backPreviewUrl ? (
            <img src={backPreviewUrl} alt="Back of insurance card" className={styles.cardThumbnail} />
          ) : (
            <>
              <UploadIcon />
              <span>{isBackUploaded ? 'Back uploaded' : 'Back of card'}</span>
            </>
          )}
        </button>
        {backError ? (
          <div className={styles.errorMessage}>
            <span>{backError}</span>
          </div>
        ) : null}
      </div>
    </div>
  );
};
